<script setup lang="ts">
import { MailIcon, PhoneIcon } from '@heroicons/vue/solid';
import type { Footer } from './types';
import { Accordion } from '~/components/ui/accordion';

const ArenaLogo = defineAsyncComponent(() => import('~/assets/images/logos/arena.vue'));
const EclipseLogo = defineAsyncComponent(() => import('~/assets/images/logos/eclipse.vue'));
const SiteLogo = defineAsyncComponent(() => import('~/assets/images/logos/Logo.vue'));

const { data, error } = await useFetch('/api/site-settings/public', {});

const { isAuthenticated } = useAuth();

const props = defineProps<Footer>();
const siteSettingsData = inject('siteSettings');

const { showArenaLogo, showEclipseLogo } = useLogo();

const logoConfigs = [
  {
    name: 'arena',
    show: showArenaLogo.value,
    component: ArenaLogo,
    path: '/arena',
    colorClass: 'text-white'
  },
  {
    name: 'eclipse',
    show: showEclipseLogo.value,
    component: EclipseLogo,
    path: '/eclipse',
    colorClass: 'text-white'
  }
];

const _email = props.email ? props.email : "sales@hdgroupuk.com";
const _phone = props.phone ? props.phone : "0800 389 7281";

const showLogos = computed(() => logoConfigs.some(config => config.show) && isAuthenticated.value);
</script>

<template>
  <footer class="flex w-full flex-col border-t-8 border-tangerine bg-gunmetal text-white print:hidden"
    role="contentinfo">
    <div class="py-10 sm:py-20">
      <div class="container flex flex-col flex-wrap gap-8 sm:flex-row">
        <div class="flex-1">
          <h4 class="pb-8 font-heading text-lg uppercase tracking-heading sm:text-nowrap sm:text-xl">Contact us</h4>
          <ul class="flex flex-col gap-3 text-sm" role="list">
            <li>
              <a :href="'mailto:' + _email" class="flex flex-row items-center hover:brightness-75"
                aria-label="Send us an email">
                <MailIcon class="size-4 stroke-2" aria-hidden="true" />
                <span class="break-all pl-4"> {{ _email }}</span>
              </a>
            </li>

            <li class="flex flex-row items-start">
              <a :href="'tel:' + _phone" class="flex flex-row items-center hover:brightness-75" aria-label="Call us">
                <PhoneIcon class="mb-auto mt-1 size-4 stroke-2" aria-hidden="true" />
                <div class="flex flex-col pl-4">
                  <span>Call us</span>
                  <span>{{ _phone }}</span>
                </div>
              </a>
            </li>

            <li class="pl-4">
              <div class="flex flex-col pl-4" role="text">
                <span>Operating hours</span>
                <span>{{ hours ? hours : "Weekdays 8:30am-5:00pm" }}</span>
              </div>
            </li>
          </ul>
        </div>

        <div v-if="!footerSection">
          <div>
            <h4 class="text-wrap pb-8 font-heading text-lg uppercase tracking-heading sm:text-xl">Useful Information
            </h4>
            <ul class="flex flex-col gap-3 text-sm" role="list">
              <li class="w-fit hover:opacity-70" key="footer1">
                <nuxt-link class="capitalize" to="/terms-and-conditions" aria-label="TermsAndConditions">Terms And Conditions</nuxt-link>
              </li>
              <li class="w-fit hover:opacity-70" key="footer2">
                <nuxt-link class="capitalize" to="/cookie-policy" aria-label="CookiePolicy">Cookie Policy</nuxt-link>
              </li>
              <li class="w-fit hover:opacity-70" key="footer3">
                <nuxt-link class="capitalize" to="/privacy-policy" aria-label="PrivacyPolicy">Privacy Policy</nuxt-link>
              </li>
              <li class="w-fit hover:opacity-70" key="footer3">
                <nuxt-link class="capitalize" to="/modern-slavery-statement" aria-label="ModernSlaveryStatement">Modern Slavery Statement</nuxt-link>
              </li>
            </ul>
          </div>
        </div>

        <div class="hidden min-w-52 flex-1 sm:block" v-for="(section, index) in footerSection"
          :key="`${section.label}-${index}`">
          <div>
            <h4 class="text-wrap pb-8 font-heading text-lg uppercase tracking-heading sm:text-xl">
              {{ section.label }}
            </h4>
            <ul class="flex flex-col gap-3 text-sm" role="list">
              <li v-for="link in section.links" class="w-fit hover:opacity-70" :key="link.title">
                <nuxt-link class="capitalize" :to="link.href" :aria-label="link.ariaLabel || link.title"
                  :target="link.target">
                  {{ link.title }}
                </nuxt-link>
              </li>
            </ul>
          </div>
        </div>
        <Accordion class="flex w-full flex-col text-white sm:hidden" type="multiple">
          <AccordionItem v-for="(section, index) in footerSection" :key="`${section.label}-${index}`"
            :value="section.label" class="mb-5 w-full border-0 first-of-type:border-b-1">
            <AccordionTrigger class="flex flex-nowrap gap-4 pb-6 text-lg font-normal uppercase sm:text-xl">
              {{ section.label }}
            </AccordionTrigger>
            <AccordionContentSSR>
              <ul class="flex flex-col gap-3 text-sm" role="list">
                <li v-for="link in section.links" :key="link.title" class="w-fit hover:opacity-70">
                  <nuxt-link class="capitalize" :to="link.href" :target="link.target"
                    :aria-label="link.ariaLabel || link.title">
                    {{ link.title }}
                  </nuxt-link>
                </li>
              </ul>
            </AccordionContentSSR>
          </AccordionItem>
        </Accordion>
        <div class="flex min-w-44 flex-1 flex-col gap-5 sm:gap-3">
          <nuxt-link href="/" class="h-fit w-fit text-white transition hover:opacity-70"
            aria-label="Return to homepage">
            <SiteLogo class="h-11 w-40" role="img" aria-label="Site logo" />
          </nuxt-link>
          <div v-show="showLogos" class="flex h-fit flex-row items-center gap-3" role="navigation"
            aria-label="Related websites">
            <Logos :logo-configs="logoConfigs" variant="footer" />
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-row justify-between rounded-tr-[160px] bg-stormcloud py-4">
      <div class="container flex flex-row flex-wrap justify-between gap-4">
        <div class="flex flex-row items-center gap-1 text-sm">
          <span>©</span><span>{{ copy ? copy : "2025 Hunter Douglas. All rights reserved." }}</span>
        </div>
        <ul class="flex flex-row flex-wrap items-center gap-4" role="list" aria-label="Social media links">
          <li v-for="link in socialLinks">
            <NavigationFooterSocialLink v-bind="link" />
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>
